/**
 * QuestionnaireForm — the PUBLIC study-questionnaire surface at /f/:token.
 *
 * Where the landing form (PublicForm's stepper) walks one question per screen,
 * a study questionnaire is the longer, sit-down form a patient fills after
 * they've already engaged — so it renders EVERY visible question on one
 * scrollable page (the recruiter wants it filled out fast). Mobile-first:
 *   - all questions in a single column, large touch targets (FormRenderer),
 *   - tapping a choice answer (yes/no, single choice, scale, height) glides
 *     the page to the next question after a short beat — long enough for the
 *     lead to SEE the selection register (solid fill + checkmark) before the
 *     scroll starts; typed fields advance on Enter instead, and
 *     multiple_choice never auto-advances (select-all-that-apply),
 *   - Enter on a text field jumps focus to the next question (enterKeyHint
 *     'next' so the phone keyboard shows a Next key); Enter on the last field
 *     submits,
 *   - answers autosave per-question (debounced) so a dropped connection or a
 *     reload never loses progress (server upsert is idempotent — same contract
 *     as the stepper), with a sticky Submit always in reach,
 *   - a value the server rejects (out of a question's min/max) shows inline
 *     under the field with the server's reason — never as a "check your
 *     connection" message — and required questions validate inline on Submit
 *     and scroll to the first gap.
 *
 * show_if branching, the WCR-faithful visibility/equality semantics, and the
 * save/complete endpoints are shared verbatim with the stepper (publicFormShared
 * + the same /answers and /complete routes) so the two surfaces never diverge.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormRenderer, { FormQuestion } from './FormRenderer';
import { API_URL, PublicQuestion, clearLocal, hasValue, isVisible, saveLocal } from './publicFormShared';

const DEBOUNCE_MS = 600;
/** Pause between a tap-committed answer and the auto-scroll to the next
 * question — the selection's fill+check needs a moment on screen first, or
 * the lead can't tell the tap registered. */
const ADVANCE_DELAY_MS = 350;

/** 'rejected' = the server validated the value and refused it (out of range);
 * distinct from 'error' (transient/network) so the two get different feedback. */
type SaveStatus = 'ok' | 'completed' | 'expired' | 'invalid' | 'error' | 'rejected';
/** Statuses flushDirty propagates to its caller — 'rejected' is handled inline
 * (per-field error) and never surfaces here. */
type FlushStatus = Exclude<SaveStatus, 'rejected'>;

interface QuestionnaireFormProps {
  token: string;
  formTitle?: string;
  questions: PublicQuestion[];
  /** Seed answers (localStorage resume — values never travel from the server). */
  initialAnswers: Record<string, any>;
  /** Keys the server already holds (cross-device resume: counts as answered
   * even when the value isn't on this device). */
  initialAnsweredKeys: string[];
  onCompleted: (message: string | null) => void;
  onExpired: () => void;
  onInvalid: () => void;
}

/** Quoted-attribute-selector escape (keys are UUIDs / form.* slugs / legacy
 * ids — only backslash and double-quote need escaping inside the quotes). */
const attrSelector = (key: string) => `[data-question-key="${key.replace(/(["\\])/g, '\\$1')}"]`;

export default function QuestionnaireForm({
  token,
  formTitle,
  questions,
  initialAnswers,
  initialAnsweredKeys,
  onCompleted,
  onExpired,
  onInvalid,
}: QuestionnaireFormProps) {
  const [answers, setAnswers] = useState<Record<string, any>>(initialAnswers);
  // savedKeys mirrors savedKeysRef into render state so the progress bar reflects
  // server-confirmed saves AND clears (the ref alone wouldn't re-render).
  const [savedKeys, setSavedKeys] = useState<Set<string>>(() => new Set(initialAnsweredKeys));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Refs beat stale closures: the debounced saver and the submit flush must act
  // on the LATEST answers and the LIVE saved/dirty/invalid sets, not a snapshot.
  const answersRef = useRef<Record<string, any>>(initialAnswers);
  const savedKeysRef = useRef<Set<string>>(new Set(initialAnsweredKeys));
  const dirtyRef = useRef<Set<string>>(new Set());
  // Keys the server rejected (bad value) and that haven't been re-edited yet —
  // they must block Submit even though they're no longer "dirty".
  const invalidRef = useRef<Set<string>>(new Set());
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Pending auto-scroll to the next question after a tap-committed answer.
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Serializes overlapping flushes (a debounce firing while a Submit flush runs)
  // so the same key is never double-posted.
  const flushPromiseRef = useRef<Promise<void> | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const qByKey = useMemo(() => {
    const m = new Map<string, PublicQuestion>();
    questions.forEach((q) => m.set(q.key, q));
    return m;
  }, [questions]);

  const visible = useMemo(() => questions.filter((q) => isVisible(q, answers)), [questions, answers]);

  // FormRenderer speaks { helpText }, the public payload speaks { description }.
  const renderQuestions: FormQuestion[] = useMemo(
    () => visible.map((q) => ({
      key: q.key,
      label: q.label,
      type: q.type,
      options: q.options,
      required: q.required,
      helpText: q.description,
      placeholder: q.placeholder,
      min: q.min,
      max: q.max,
    })),
    [visible]
  );

  const answeredCount = visible.filter((q) => hasValue(answers[q.key]) || savedKeys.has(q.key)).length;
  const progress = visible.length > 0 ? Math.round((answeredCount / visible.length) * 100) : 0;

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
  }, []);

  const commitSaved = useCallback(() => setSavedKeys(new Set(savedKeysRef.current)), []);

  /** POST one answer (value null clears it server-side). 'rejected' carries the
   * server's reason so the caller can show it inline on the field. */
  const saveAnswer = useCallback(async (q: PublicQuestion, value: any): Promise<{ status: SaveStatus; detail?: string }> => {
    try {
      const res = await fetch(`${API_URL}/api/crm/public/forms/${token}/answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_key: q.key, value: value === undefined ? null : value }),
      });
      if (res.ok) return { status: 'ok' };
      const body = await res.json().catch(() => ({}));
      if (res.status === 409 && body?.error === 'already_completed') return { status: 'completed' };
      if (res.status === 404) return { status: body?.error === 'link_expired' ? 'expired' : 'invalid' };
      if (res.status === 400 && body?.error === 'invalid_value') return { status: 'rejected', detail: body?.detail };
      return { status: 'error' };
    } catch {
      return { status: 'error' };
    }
  }, [token]);

  /** Persist every dirty answer. Serialized via flushPromiseRef so a debounce
   * and a Submit can't post the same key twice. A rejected value is flagged
   * inline and dropped from the dirty set (so it never wedges Submit on a
   * transient-error guard); the returned status reflects only flush-level
   * outcomes so the caller can navigate on terminal states. */
  const flushDirty = useCallback(async (): Promise<FlushStatus> => {
    while (flushPromiseRef.current) await flushPromiseRef.current;
    let resolveDone!: () => void;
    flushPromiseRef.current = new Promise<void>((r) => { resolveDone = r; });
    let status: FlushStatus = 'ok';
    try {
      const keys = Array.from(dirtyRef.current);
      for (const key of keys) {
        if (!dirtyRef.current.has(key)) continue;
        const q = qByKey.get(key);
        if (!q) { dirtyRef.current.delete(key); continue; }
        const value = answersRef.current[key];
        const { status: result, detail } = await saveAnswer(q, value);
        if (result === 'ok') {
          dirtyRef.current.delete(key);
          invalidRef.current.delete(key);
          if (hasValue(value)) savedKeysRef.current.add(key);
          else savedKeysRef.current.delete(key);
          commitSaved();
        } else if (result === 'rejected') {
          // The server refused this value (out of range). Surface its reason on
          // the field and stop tracking the key as dirty/saved — editing it
          // re-queues a save. Keep flushing the rest.
          dirtyRef.current.delete(key);
          savedKeysRef.current.delete(key);
          invalidRef.current.add(key);
          commitSaved();
          setErrors((e) => ({ ...e, [key]: detail || 'That answer isn’t valid.' }));
        } else {
          // Terminal (expired/invalid/completed) or a transient error — stop and
          // surface it; the unsaved keys stay dirty for the next attempt.
          if (result === 'error') setNotice('Some answers could not be saved — check your connection and try again.');
          status = result;
          break;
        }
      }
    } finally {
      flushPromiseRef.current = null;
      resolveDone();
    }
    return status;
  }, [qByKey, saveAnswer, commitSaved]);

  const runAutosave = useCallback(async () => {
    const status = await flushDirty();
    if (status === 'expired') onExpired();
    else if (status === 'invalid') onInvalid();
    else if (status === 'completed') { clearLocal(token); onCompleted(null); }
  }, [flushDirty, onExpired, onInvalid, onCompleted, token]);

  const onAnswer = useCallback((key: string, value: any) => {
    const next = { ...answersRef.current, [key]: value };
    answersRef.current = next;
    setAnswers(next);
    saveLocal(token, next);
    setNotice(null);
    // Any edit cancels a pending auto-scroll — the lead is clearly interacting
    // where they are. A tap-commit re-schedules right after (onChoiceCommit
    // fires after onChange), so choice answers still advance.
    if (advanceTimer.current) { clearTimeout(advanceTimer.current); advanceTimer.current = null; }
    // Editing optimistically clears the field's error + invalid flag; a re-save
    // that still fails re-flags it.
    invalidRef.current.delete(key);
    setErrors((e) => (e[key] ? { ...e, [key]: '' } : e));
    dirtyRef.current.add(key);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { runAutosave(); }, DEBOUNCE_MS);
  }, [token, runAutosave]);

  /** Bring a question into view and focus its first control (scroll-to-error
   * on Submit, focus-next on Enter). */
  const focusQuestion = useCallback((key: string, { focus = true }: { focus?: boolean } = {}) => {
    const block = formRef.current?.querySelector(attrSelector(key));
    if (!block) return;
    block.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (!focus) return;
    const el = block.querySelector('input, select, textarea, button') as HTMLElement | null;
    el?.focus({ preventScroll: true });
  }, []);

  /** A tap fully committed an answer (FormRenderer's onChoiceCommit) → glide
   * to the next visible question after a beat. Scroll only, no focus steal —
   * focusing a text field here would pop the phone keyboard mid-scroll.
   * Visibility is recomputed from the just-updated answers, so a show_if
   * follow-up revealed by this very answer becomes the scroll target. */
  const scheduleAdvance = useCallback((key: string) => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      advanceTimer.current = null;
      const vis = questions.filter((q) => isVisible(q, answersRef.current));
      const i = vis.findIndex((q) => q.key === key);
      const next = i >= 0 ? vis[i + 1] : undefined;
      if (next) focusQuestion(next.key, { focus: false });
    }, ADVANCE_DELAY_MS);
  }, [questions, focusQuestion]);

  const submit = useCallback(async () => {
    if (submitting) return;
    setNotice(null);
    // Instant client-side required check (the server is still authoritative).
    const live = answersRef.current;
    const missing = questions.filter((q) =>
      q.required && isVisible(q, live) && !hasValue(live[q.key]) && !savedKeysRef.current.has(q.key));
    if (missing.length > 0) {
      const errs: Record<string, string> = {};
      missing.forEach((q) => { errs[q.key] = 'This question needs an answer.'; });
      setErrors((e) => ({ ...e, ...errs }));
      setNotice('Please answer the highlighted questions.');
      focusQuestion(missing[0].key);
      return;
    }
    setSubmitting(true);
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }

    const flushed = await flushDirty();
    if (flushed === 'expired') { onExpired(); return; }
    if (flushed === 'invalid') { onInvalid(); return; }
    if (flushed === 'completed') { clearLocal(token); onCompleted(null); return; }
    if (flushed === 'error' || dirtyRef.current.size > 0) {
      setNotice('Some answers could not be saved — check your connection and try again.');
      setSubmitting(false);
      return;
    }
    // A value the server rejected blocks completion until it's fixed.
    if (invalidRef.current.size > 0) {
      const firstBadKey = visible.find((q) => invalidRef.current.has(q.key))?.key || Array.from(invalidRef.current)[0];
      setNotice('Please fix the highlighted answers.');
      if (firstBadKey) focusQuestion(firstBadKey);
      setSubmitting(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/crm/public/forms/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) { clearLocal(token); onCompleted(body?.completion_message || null); return; }
      if (res.status === 400 && body?.error === 'missing_required' && Array.isArray(body.keys) && body.keys.length > 0) {
        const errs: Record<string, string> = {};
        body.keys.forEach((k: string) => { errs[k] = 'This question needs an answer.'; });
        setErrors((e) => ({ ...e, ...errs }));
        setNotice('A few required questions still need an answer.');
        focusQuestion(body.keys[0]);
        setSubmitting(false);
        return;
      }
      if (res.status === 404) { body?.error === 'link_expired' ? onExpired() : onInvalid(); return; }
      if (res.status === 409 && body?.error === 'already_completed') { clearLocal(token); onCompleted(null); return; }
      setNotice('Something went wrong submitting your answers. Please try again.');
      setSubmitting(false);
    } catch {
      setNotice('Connection problem — please check your signal and try again.');
      setSubmitting(false);
    }
  }, [submitting, questions, visible, flushDirty, focusQuestion, onExpired, onInvalid, onCompleted, token]);

  // Enter on a text field advances to the next visible question (mobile "next");
  // on the last field it submits. textareas keep their newline; choice/scale
  // buttons handle their own Enter.
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement;
    if (!target || target.tagName !== 'INPUT') return;
    e.preventDefault();
    const block = target.closest('[data-question-key]');
    const key = block?.getAttribute('data-question-key');
    if (!key) return;
    const vis = questions.filter((q) => isVisible(q, answersRef.current));
    const i = vis.findIndex((q) => q.key === key);
    const next = i >= 0 ? vis[i + 1] : undefined;
    if (next) focusQuestion(next.key);
    else submit(); // last field — Enter submits
  }, [questions, focusQuestion, submit]);

  return (
    <div>
      {/* progress header */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
          <span className="truncate pr-2">{formTitle}</span>
          <span className="shrink-0">{answeredCount} of {visible.length} answered</span>
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
          <div className="h-full rounded-full bg-primary-600 transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {notice && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          {notice}
        </div>
      )}

      {/* A <form> (not a bare div) so mobile autofill spans the fields; Enter is
          handled manually and onSubmit is neutralized so a stray select-Enter
          can't submit early. pb-24 keeps the last question clear of the sticky bar. */}
      <form ref={formRef} onSubmit={(e) => e.preventDefault()} onKeyDown={onKeyDown} className="pb-24">
        <FormRenderer
          mode="page"
          questions={renderQuestions}
          answers={answers}
          onAnswer={onAnswer}
          onChoiceCommit={scheduleAdvance}
          readOnly={submitting}
          errors={errors}
          enterKeyHint="next"
        />
      </form>

      {/* Sticky Submit — flush to the card edges, always in reach on mobile. */}
      <div className="sticky bottom-0 -mx-5 -mb-5 rounded-b-2xl border-t border-gray-200 bg-white/95 px-5 py-3 backdrop-blur dark:border-gray-700 dark:bg-gray-800/95">
        <button
          ref={submitRef}
          type="button"
          onClick={submit}
          disabled={submitting}
          className="min-h-[48px] w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
